import { useState, useEffect } from "react";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { CHARACTER_LENGTH } from "@constants/index";
import { OutlineButton, PrimaryButton } from "@components/Buttons";

const AddCommentModal = ({
    isOpen,
    onClose,
    onSubmit,
    verificationData,
    isPending = false,
    isEditMode = false,
    existingCommentData = null,
    "data-testid": dataTestId = "add-comment-modal",
}) => {
    const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
    const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
    const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
    const fieldErrorTestId = (name) => `${dataTestId}-field-${name}-error`;
    const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
    const { colors } = useTheme();
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        commentMessage: "",
        attachment: null,
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen && isEditMode && existingCommentData) {
            setFormData({
                commentMessage: existingCommentData.commentMessage || "",
                attachment: existingCommentData.attachment || null,
            });
            setErrors({});
        } else if (isOpen && !isEditMode) {
            setFormData({
                commentMessage: "",
                attachment: null,
            });
            setErrors({});
        }
    }, [isOpen, isEditMode, existingCommentData]);

    const [originalAttachment, setOriginalAttachment] = useState(null);
    const [hasChangedAttachment, setHasChangedAttachment] = useState(false);

    useEffect(() => {
        if (isOpen && isEditMode && existingCommentData) {
            setOriginalAttachment(existingCommentData.attachment || null);
            setHasChangedAttachment(false);
        } else {
            setOriginalAttachment(null);
            setHasChangedAttachment(false);
        }
    }, [isOpen, isEditMode, existingCommentData]);

    const isFormValid = formData.commentMessage.trim() && !errors.commentMessage;

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.commentMessage.trim()) {
            newErrors.commentMessage = t("communication.comment-message-required");
        }

        if (formData.commentMessage.length > CHARACTER_LENGTH.COMMENT) {
            newErrors.commentMessage = t("communication.comment-message-too-long");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            if (onSubmit) {
                const finalAttachment = isEditMode && !hasChangedAttachment && originalAttachment
                    ? originalAttachment
                    : formData.attachment;

                onSubmit({
                    commentMessage: formData.commentMessage.trim(),
                    attachment: finalAttachment,
                });
            }
            setFormData({
                commentMessage: "",
                attachment: null,
            });
            setErrors({});
        }
    };

    const handleClose = () => {
        setFormData({
            commentMessage: "",
            attachment: null,
        });
        setErrors({});
        onClose();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                attachment: file
            }));
            setHasChangedAttachment(true);
        }
    };

    const shouldShowAttachment = () => {
        return verificationData?.verificationStatusCd === "STANDBY_FOLLOWUP" ||
            verificationData?.verificationStatusCd === "ON_REVIEW";
    };

    if (!isOpen) return null;

    return (
        <div
            className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
            data-testid={`${dataTestId}-overlay`}
        >
            <div
                className={`border ${colors.borderPrimary} ${colors.bgModal} min-h-[50vh] max-h-[80vh] w-[800px] min-w-[800px] flex-shrink-0 flex flex-col rounded-lg shadow-xl`}
                data-testid={dataTestId}
            >
                {/* Header */}
                <div className="px-6 py-6 flex-shrink-0" data-testid={`${dataTestId}-header`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
                                {isEditMode ? t("communication.edit-comment-title") : t("communication.add-comment-title")}
                            </h2>
                            {verificationData?.verificationStatusCd && (
                                <span
                                    className={`rounded-lg px-3 py-1 text-sm font-medium ${colors.bgStatusSuccess} ${colors.textOnPrimary}`}
                                    data-testid={fieldValueTestId("verification-status")}
                                >
                                    {verificationData.verificationStatusCd === "ON_VERIFICATION" && verificationData.verificationStatusCdName === "Usage Type Input" ? t("status.usage-type-input") :
                                        verificationData.verificationStatusCd === "ON_VERIFICATION" && verificationData.verificationStatusCdName === "Usage Type Review" ? t("status.request-usage-type-review") :
                                            verificationData.verificationStatusCd === "ON_VERIFICATION" && verificationData.verificationStatusCdName === "Usage Type Check" ? t("status.usage-type-review") :
                                                verificationData.verificationStatusCd === "ON_REVIEW" ? t("common.review") :
                                                    verificationData.verificationStatusCd === "STANDBY_FOLLOWUP" ? t("status.standbyFollowup") :
                                                        verificationData.verificationStatusCd === "FOLLOWUP_CHECK" ? t("status.followupCheck") :
                                                            verificationData.verificationStatusCd === "ANALYSIS_CHECK" ? t("status.analysisCheck") :
                                                                verificationData.verificationStatusCd}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content - Scrollable */}
                <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden" data-testid={`${dataTestId}-form`}>
                    <div className="flex-1 overflow-y-auto px-6 space-y-4" data-testid={`${dataTestId}-content`}>
                        {/* Comment Message */}
                        <div data-testid={fieldContainerTestId("comment-message")}>
                            <label
                                className={`mb-2 block text-sm font-medium ${colors.textPrimary}`}
                                data-testid={fieldLabelTestId("comment-message")}
                            >
                                {t("communication.comment-message")} <span className={colors.textDanger}>*</span>
                            </label>
                            <textarea
                                data-testid={fieldInputTestId("comment-message")}
                                value={formData.commentMessage}
                                onChange={(e) => handleInputChange("commentMessage", e.target.value)}
                                placeholder={t("communication.write-your-comments")}
                                maxLength={CHARACTER_LENGTH.COMMENT}
                                className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${errors.commentMessage ? colors.borderDangerOutLineButton : colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                                rows="10"
                                required
                            />
                            {errors.commentMessage && (
                                <p className={`${colors.textDanger} text-xs mt-1`} data-testid={fieldErrorTestId("comment-message")}>
                                    {errors.commentMessage}
                                </p>
                            )}
                            <p className={`text-xs ${colors.textMuted} mt-1`} data-testid={fieldValueTestId("comment-message-char-count")}>
                                {formData.commentMessage.length}/{CHARACTER_LENGTH.COMMENT}
                            </p>
                        </div>

                        {/* Attachment Section */}
                        {shouldShowAttachment() && (
                            <div data-testid={fieldContainerTestId("attachment")}>
                                <label
                                    className={`mb-2 block text-sm font-medium ${colors.textPrimary}`}
                                    data-testid={fieldLabelTestId("attachment")}
                                >
                                    {t("communication.attachment")}
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        id="file-upload"
                                        accept="*/*"
                                        data-testid={fieldInputTestId("attachment")}
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className={`flex items-center gap-2 cursor-pointer ${colors.textActive} ${colors.textHoverActive} transition-colors`}
                                        data-testid={fieldInputTestId("attachment-browse")}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <span className="text-sm font-medium">{t("communication.browse-file")}</span>
                                    </label>
                                    {(formData.attachment || (isEditMode && originalAttachment && !hasChangedAttachment)) && (
                                        <div className="flex items-center gap-2" data-testid={fieldValueTestId("attachment-filename")}>
                                            <span className={`text-sm ${colors.textMuted}`}>
                                                {formData.attachment?.name ||
                                                    formData.attachment?.fileName ||
                                                    originalAttachment?.fileName ||
                                                    originalAttachment?.name ||
                                                    "Unknown file"}
                                            </span>
                                            {isEditMode && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, attachment: null }));
                                                        setHasChangedAttachment(true);
                                                    }}
                                                    className={`${colors.textDanger} ${colors.hoverTextDanger} text-sm`}
                                                    data-testid={fieldInputTestId("attachment-remove")}
                                                >
                                                    {t("communication.remove-file")}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer - Fixed at bottom */}
                    <div className="flex-shrink-0 py-4" data-testid={`${dataTestId}-footer`}>
                        <div className={`h-px w-full ${colors.bgDivider} mb-4`}></div>

                        <div className={`${colors.borderPrimary} px-6`}>
                            <div className="flex items-center justify-end">
                                <div className="flex items-center space-x-3">
                                    <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleClose}>
                                        {t("common.cancel-button")}
                                    </OutlineButton>
                                    <PrimaryButton
                                        data-testid={`${dataTestId}-confirm-button`}
                                        onClick={handleSubmit}
                                        disabled={isPending || !isFormValid}
                                    >
                                        {isPending ? (
                                            <div className="flex items-center">
                                                <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderOnPrimary}`}></div>
                                                {t("communication.sending")}
                                            </div>
                                        ) : (
                                            isEditMode ? t("communication.update-comment") : t("communication.add-comment")
                                        )}
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddCommentModal;
